import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getBfixSpecialDays,
  saveBfixSpecialDaysBatch,
} from '../../api/bfixSpecialDays';
import { useBfixRateTypes } from '../../hooks/useBfixRateTypes';
import type { BFIXSpecialDay } from '../../types/BFIXSpecialDay';
import { dateColumnProps } from '../../utils/dateColumn';
import { t } from '../../i18n/t';

const newBfixSpecialDayDefaults = (): Partial<BFIXSpecialDay> => ({
  dDate: '',
  // Default to "1 - רגיל" so the dropdown shows a valid value on Add.
  bfixRateTypeId: 1,
  aComment: '',
});

export function BFIXSpecialDaysPage() {
  // Lazy load: rate types fetched only when this page mounts.
  const {
    data: rateTypes,
    loading: typesLoading,
    error: typesError,
  } = useBfixRateTypes();

  const columnDefs = useMemo<ColDef<BFIXSpecialDay>[]>(() => {
    if (!rateTypes) return [];
    // Per LOP convention: "<id> - <name>" labels (even though current names
    // are unique — keeps it informative and survives future name collisions).
    const labelById = new Map<number, string>(
      rateTypes.map((r) => [r.bfixRateTypeId, `${r.bfixRateTypeId} - ${r.bfixRateTypeName}`]),
    );
    const idByLabel = new Map<string, number>(
      rateTypes.map((r) => [`${r.bfixRateTypeId} - ${r.bfixRateTypeName}`, r.bfixRateTypeId]),
    );
    const dropdownValues = Array.from(labelById.values());

    return [
      {
        field: 'dDate',
        headerName: t('bfixSpecialDays.columns.dDate'),
        width: 140,
        // Natural PK: editable on Add only.
        editable: (params: EditableCallbackParams<BFIXSpecialDay>) =>
          (params.data as { __status?: string } | undefined)?.__status === 'added',
        ...dateColumnProps<BFIXSpecialDay>('dDate'),
        sort: 'desc',
      },
      {
        field: 'bfixRateTypeId',
        headerName: t('bfixSpecialDays.columns.bfixRateTypeId'),
        width: 180,
        editable: true,
        // Per CLAUDE.md AgGrid dropdown rule: cellDataType:'text' is REQUIRED
        // when the underlying field is numeric but the editor speaks strings.
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: dropdownValues },
        valueGetter: (p: ValueGetterParams<BFIXSpecialDay>) => {
          if (!p.data) return '';
          // Orphan-id fallback: if the rate type isn't in the loaded lookup
          // (e.g. dirty data), show the raw id so the user still sees it.
          return labelById.get(p.data.bfixRateTypeId) ?? String(p.data.bfixRateTypeId ?? '');
        },
        valueSetter: (p: ValueSetterParams<BFIXSpecialDay>) => {
          if (!p.data) return false;
          const newId = idByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.bfixRateTypeId = newId;
          return true;
        },
      },
      {
        field: 'aComment',
        headerName: t('bfixSpecialDays.columns.aComment'),
        flex: 1,
        editable: true,
      },
    ];
  }, [rateTypes]);

  if (typesError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('bfixSpecialDays.errorPrefix', { message: typesError })}
        </div>
      </div>
    );
  }

  if (typesLoading || !rateTypes) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <LopCrudPage<BFIXSpecialDay>
      resourceCode="bfixSpecialDays"
      titleKey="bfixSpecialDays.pageTitle"
      idField="dDate"
      getRows={getBfixSpecialDays}
      saveBatch={(added, modified, deletedIds) =>
        saveBfixSpecialDaysBatch(added, modified, deletedIds as string[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newBfixSpecialDayDefaults}
    />
  );
}
